import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Bell, Calendar, Users, AlertTriangle, Check, ChevronRight, Building, ExternalLink } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * User Coordination Message Inbox
 * 
 * UI/UX Directive:
 * - Messages appear as STRUCTURED NOTICES, not chat bubbles
 * - No chat-style UI
 * - Shows message type label, institution name, timestamp, content
 * - Response signals (if enabled) are single-click participation indicators
 */

const MESSAGE_TYPE_CONFIG = {
  announcement: { label: 'Announcement', icon: Bell, color: 'var(--lk-sage)' },
  reminder: { label: 'Reminder', icon: Calendar, color: 'var(--lk-earth)' },
  coordination: { label: 'Coordination', icon: Users, color: 'var(--lk-secondary)' },
  urgent_notice: { label: 'Urgent Notice', icon: AlertTriangle, color: '#D97706' },
  institution_announcement: { label: 'Announcement', icon: Bell, color: 'var(--lk-sage)' },
  institution_reminder: { label: 'Reminder', icon: Calendar, color: 'var(--lk-earth)' },
  institution_coordination: { label: 'Coordination', icon: Users, color: 'var(--lk-secondary)' },
  institution_urgent_notice: { label: 'Urgent Notice', icon: AlertTriangle, color: '#D97706' }
};

const RESPONSE_SIGNALS = [
  { id: 'attending', label: "I'm attending", icon: Check },
  { id: 'can_help', label: 'I can help', icon: Users },
  { id: 'bringing_supplies', label: 'Bringing supplies', icon: null },
  { id: 'need_ride', label: 'Need a ride', icon: null },
  { id: 'not_attending', label: 'Not attending', icon: null }
];

export default function CoordinationInbox() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [responding, setResponding] = useState(false);
  
  useEffect(() => {
    fetchMessages();
  }, []);
  
  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/api/institution-messages/inbox?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRespond = async (messageId, signalId) => {
    setResponding(true);
    try {
      const res = await fetch(`${API}/api/institution-messages/messages/${messageId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ signal_id: signalId })
      });
      
      if (res.ok) {
        // Update local state
        setMessages(prev => prev.map(m => {
          if (m.source_id === messageId) {
            return { ...m, has_responded: true, response: signalId };
          }
          return m;
        }));
        
        if (selectedMessage?.source_id === messageId) {
          setSelectedMessage(prev => ({ ...prev, has_responded: true, response: signalId }));
        }
      }
    } catch (err) {
      console.error('Failed to respond:', err);
    } finally {
      setResponding(false);
    }
  };
  
  const handleAcknowledge = async (messageId) => {
    setResponding(true);
    try {
      const res = await fetch(`${API}/api/institution-messages/messages/${messageId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acknowledgment_type: 'seen' })
      });
      
      if (res.ok) {
        // Update local state
        setMessages(prev => prev.map(m => {
          if (m.source_id === messageId) {
            return { ...m, has_acknowledged: true };
          }
          return m;
        }));
        
        if (selectedMessage?.source_id === messageId) {
          setSelectedMessage(prev => ({ ...prev, has_acknowledged: true }));
        }
      }
    } catch (err) {
      console.error('Failed to acknowledge:', err);
    } finally {
      setResponding(false);
    }
  };
  
  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 hover:opacity-70" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Inbox</h1>
            <div className="w-10" />
          </div>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-4 py-6">
        {messages.length === 0 ? (
          <Card className="p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
            <Bell className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
            <p className="font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
              No messages
            </p>
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              Messages from your institutions and groups will appear here
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => {
              const typeKey = msg.type || msg.message?.message_type || 'announcement';
              const config = MESSAGE_TYPE_CONFIG[typeKey] || MESSAGE_TYPE_CONFIG.announcement;
              const Icon = config.icon;
              const isUnread = !msg.is_read;
              
              return (
                <Card
                  key={msg.notification_id}
                  className="overflow-hidden"
                  style={{ 
                    backgroundColor: isUnread ? 'var(--fy-surface-card)' : 'var(--fy-background-secondary)',
                    border: isUnread ? '2px solid var(--lk-sage)' : '1px solid var(--fy-border-subtle)'
                  }}
                  data-testid={`inbox-message-${msg.notification_id}`}
                >
                  {/* Structured Notice Header */}
                  <div className="p-4" style={{ borderBottom: '1px solid var(--fy-border-subtle)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span 
                        className="text-xs font-medium px-2 py-0.5 rounded uppercase"
                        style={{ backgroundColor: `${config.color}15`, color: config.color }}
                      >
                        {config.label}
                      </span>
                      {isUnread && (
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--lk-sage)' }} />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-1">
                      <Building className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
                      <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                        {msg.institution_name || 'Institution'}
                      </span>
                    </div>
                    
                    <span className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                      {formatDate(msg.created_at)}
                    </span>
                  </div>
                  
                  {/* Message Content */}
                  <div className="p-4">
                    <h3 className="font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                      {msg.title || msg.message?.subject}
                    </h3>
                    <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--fy-text-secondary)' }}>
                      {msg.body || msg.message?.body}
                    </p>
                    
                    {/* Urgent Notice Disclaimer */}
                    {(typeKey === 'urgent_notice' || typeKey === 'institution_urgent_notice') && (
                      <p className="text-xs mt-3 pt-3 italic" style={{ color: 'var(--fy-text-disabled)', borderTop: '1px solid var(--fy-border-subtle)' }}>
                        LikeKind is a coordination tool. This message should not be relied upon as a replacement for emergency services.
                      </p>
                    )}
                    
                    {/* Related Event Link */}
                    {msg.message?.related_event_id && (
                      <Link 
                        to={`/events/${msg.message.related_event_id}`}
                        className="inline-flex items-center gap-1 mt-3 text-sm"
                        style={{ color: 'var(--lk-sage)' }}
                      >
                        <Calendar className="w-4 h-4" />
                        View Related Event
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                  
                  {/* Response Signals (for coordination messages) */}
                  {msg.message?.enable_responses && !msg.has_responded && (
                    <div className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', borderTop: '1px solid var(--fy-border-subtle)' }}>
                      <p className="text-xs mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
                        Let the organizer know:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {RESPONSE_SIGNALS.filter(s => 
                          !msg.message.response_options || msg.message.response_options.includes(s.id)
                        ).map(signal => (
                          <button
                            key={signal.id}
                            onClick={() => handleRespond(msg.source_id, signal.id)}
                            disabled={responding}
                            className="px-3 py-1.5 rounded-full text-sm transition-colors"
                            style={{
                              backgroundColor: 'var(--fy-surface-card)',
                              border: '1px solid var(--fy-border-subtle)',
                              color: 'var(--fy-text-primary)'
                            }}
                            data-testid={`respond-${signal.id}`}
                          >
                            {signal.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Response Confirmed */}
                  {msg.has_responded && (
                    <div className="p-4 flex items-center gap-2" style={{ backgroundColor: 'var(--lk-sage-muted)', borderTop: '1px solid var(--fy-border-subtle)' }}>
                      <Check className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
                      <span className="text-sm" style={{ color: 'var(--lk-sage-dark)' }}>
                        You responded: {RESPONSE_SIGNALS.find(s => s.id === msg.response)?.label || msg.response}
                      </span>
                    </div>
                  )}
                  
                  {/* Acknowledgment Required */}
                  {msg.message?.requires_acknowledgment && !msg.has_acknowledged && (
                    <div className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', borderTop: '1px solid var(--fy-border-subtle)' }}>
                      <Button
                        onClick={() => handleAcknowledge(msg.source_id)}
                        disabled={responding}
                        className="w-full"
                        style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
                        data-testid="acknowledge-button"
                      >
                        I Acknowledge
                      </Button>
                    </div>
                  )}
                  
                  {/* Acknowledged */}
                  {msg.has_acknowledged && (
                    <div className="p-4 flex items-center gap-2" style={{ backgroundColor: 'var(--lk-sage-muted)', borderTop: '1px solid var(--fy-border-subtle)' }}>
                      <Check className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
                      <span className="text-sm" style={{ color: 'var(--lk-sage-dark)' }}>
                        Acknowledged
                      </span>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
